import React, { useState } from 'react';
import './App.css';
import GameInitializer, {GameSession} from './components/GameInitializer';
import GameInterface from './components/GameInterface';
import { CookieManager } from './utils/cookieManager';
import { WebSocketProvider } from './contexts/WebSocketContext';

function App() {
  const [gameSession, setGameSession] = useState<GameSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingResume, setLoadingResume] = useState(false);

  const handleGameStart = (gameSession: GameSession) => {
    setGameSession(gameSession);
    setError(null);
  };

  const handleNewGame = () => {
    setGameSession(null);
    setError(null);
  };

  const handleResumeGame = async (gameId: string, primers: string[]) => {
    setLoadingResume(true);
    setError(null);

    try {
      // Fetch game state via HTTP API
      const response = await fetch(`https://api.unchained.coljor.com/game/${gameId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Game not found. It may have expired.');
        }
        throw new Error('Failed to load game');
      }
      
      const data = await response.json();
      console.log('Received game state:', data);
      
      // Create game session from the retrieved state
      const session = data.session;
      const gameSession: GameSession = {
        gameId: gameId,
        primers: primers || session.primers || [],
        worldContext: session.worldContext || '',
        winConditions: session.winConditions || '',
        currentScene: session.scene || '',
        sceneDescription: session.sceneSummary || '',
        imageUrl: session.imageUrl || '',
        isComplete: session.gameStatus !== '<---QUEST_IN_PROGRESS--->'
      };
      
      console.log('Created game session:', gameSession);
      
      // Update cookie with last played time
      CookieManager.updateLastPlayed(gameId);
      
      setGameSession(gameSession);
      setLoadingResume(false);
      
    } catch (err) {
      console.error('Failed to resume game:', err);
      setError(err instanceof Error ? err.message : 'Failed to resume game');
      setLoadingResume(false);
    }
  };

  return (
    <WebSocketProvider>
      <div className="App">
        <main className="App-main">
          {error && (
            <div className="error-message">
              <p>Error: {error}</p>
              <button onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          {!gameSession && (
            <GameInitializer 
              onGameStart={handleGameStart}
              onResumeGame={handleResumeGame}
            />
          )}
          
          {loadingResume && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-message">Resuming your adventure...</div>
            </div>
          )}
          
          {gameSession && (
            <GameInterface 
              gameSession={gameSession} 
              onNewGame={handleNewGame}
            />
          )}
        </main>
      </div>
    </WebSocketProvider>
  );
}

export default App;
